import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { toast } from 'sonner';
import { Loader2, Lock, ArrowRight } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const LOGO_URL = "";

export default function Login() {
  const navigate = useNavigate();
  const { login, register } = useAuth();
  const [loading, setLoading] = useState(false);
  const [loginData, setLoginData] = useState({ email: '', password: '' });
  const [registerData, setRegisterData] = useState({ name: '', email: '', password: '', confirmPassword: '' });

  // Gate state
  const [gateEnabled, setGateEnabled] = useState(null); // null = loading, true/false
  const [gateUnlocked, setGateUnlocked] = useState(false);
  const [gatePassword, setGatePassword] = useState('');
  const [gateLoading, setGateLoading] = useState(false);

  useEffect(function() {
    // Check if already unlocked this session
    if (sessionStorage.getItem('portal_gate_unlocked') === 'true') {
      setGateUnlocked(true);
      setGateEnabled(true);
      return;
    }
    // Check if gate is enabled
    axios.get(API_URL + '/api/portal/gate-check').then(function(r) {
      setGateEnabled(r.data.gate_enabled);
      if (!r.data.gate_enabled) setGateUnlocked(true);
    }).catch(function() { setGateEnabled(false); setGateUnlocked(true); });
  }, []);

  function handleGateSubmit(e) {
    e.preventDefault();
    if (!gatePassword) { toast.error('Please enter the access password'); return; }
    setGateLoading(true);
    axios.post(API_URL + '/api/portal/gate-verify', { password: gatePassword }).then(function() {
      sessionStorage.setItem('portal_gate_unlocked', 'true');
      setGateUnlocked(true);
      toast.success('Access granted');
    }).catch(function() {
      toast.error('Incorrect access password');
    }).finally(function() { setGateLoading(false); });
  }

  const handleLogin = async (e) => {
    e.preventDefault();
    if (!loginData.email || !loginData.password) {
      toast.error('Please fill in all fields');
      return;
    }
    
    setLoading(true);
    try {
      await login(loginData.email, loginData.password);
      toast.success('Welcome back!');
      navigate('/dashboard');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Login failed');
    } finally {
      setLoading(false);
    }
  };

  const handleRegister = async (e) => {
    e.preventDefault();
    if (!registerData.name || !registerData.email || !registerData.password) {
      toast.error('Please fill in all fields');
      return;
    }
    if (registerData.password !== registerData.confirmPassword) {
      toast.error('Passwords do not match');
      return;
    }
    if (registerData.password.length < 6) {
      toast.error('Password must be at least 6 characters');
      return;
    }

    setLoading(true);
    try {
      const result = await register(registerData.name, registerData.email, registerData.password);
      toast.success(result.message || 'Registration submitted! Awaiting admin approval.');
      setRegisterData({ name: '', email: '', password: '', confirmPassword: '' });
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Registration failed');
    } finally {
      setLoading(false);
    }
  };

  // Loading state
  if (gateEnabled === null) {
    return <div className="min-h-screen flex items-center justify-center bg-slate-50"><Loader2 className="w-8 h-8 animate-spin text-primary" /></div>;
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-slate-50">
      <div className="w-full max-w-md p-8">
        <div className="mb-8 text-center">
          <div className="mx-auto w-16 h-16 rounded-full bg-primary/10 flex items-center justify-center">
            <Lock className="w-8 h-8 text-primary" />
          </div>
          <h1 className="mt-3 text-2xl font-bold text-slate-800">Power Compare</h1>
          <p className="text-sm text-slate-500">Staff Portal</p>
        </div>

          {/* GATE SCREEN */}
          {gateEnabled && !gateUnlocked ? (
            <Card className="border-0 shadow-xl" data-testid="gate-card">
              <CardHeader className="text-center pb-2">
                <div className="mx-auto w-14 h-14 rounded-full bg-primary/10 flex items-center justify-center mb-3">
                  <Lock className="w-7 h-7 text-primary" />
                </div>
                <CardTitle className="text-2xl font-bold text-slate-900">Login Required</CardTitle>
                <CardDescription>Enter the access password to continue</CardDescription>
              </CardHeader>
              <CardContent>
                <form onSubmit={handleGateSubmit} className="space-y-4">
                  <div className="space-y-2">
                    <Label htmlFor="gate-password">Access Password</Label>
                    <Input
                      id="gate-password"
                      type="password"
                      placeholder="Enter portal access password"
                      value={gatePassword}
                      onChange={function(e) { setGatePassword(e.target.value); }}
                      className="h-12"
                      autoFocus
                      data-testid="gate-password-input"
                    />
                  </div>
                  <Button type="submit" className="w-full h-12 rounded-full bg-primary hover:bg-primary/90 font-semibold" disabled={gateLoading} data-testid="gate-submit-btn">
                    {gateLoading ? <Loader2 className="w-5 h-5 animate-spin" /> : <><span>Continue</span><ArrowRight className="w-4 h-4 ml-2" /></>}
                  </Button>
                </form>
                <p className="text-center text-sm text-slate-500 mt-4" data-testid="gate-notice">
                  Staff access only. <a href="/" className="text-primary underline hover:text-primary/80">Go to comparison tool</a>.
                </p>
              </CardContent>
            </Card>
          ) : (
            /* LOGIN / REGISTER FORM */
            <>
              <Card className="border-0 shadow-xl">
                <CardHeader className="text-center pb-2">
                  <CardTitle className="text-2xl font-bold text-slate-900">Welcome</CardTitle>
                  <CardDescription>Sign in to access the staff portal</CardDescription>
                </CardHeader>
                <CardContent>
              <Tabs defaultValue="login" className="w-full">
                <TabsList className="grid w-full grid-cols-2 mb-6">
                  <TabsTrigger value="login" data-testid="login-tab">Login</TabsTrigger>
                  <TabsTrigger value="register" data-testid="register-tab">Register</TabsTrigger>
                </TabsList>

                <TabsContent value="login">
                  <form onSubmit={handleLogin} className="space-y-4">
                    <div className="space-y-2">
                      <Label htmlFor="login-email">Email</Label>
                      <Input
                        id="login-email"
                        type="email"
                        placeholder="you@company.com"
                        value={loginData.email}
                        onChange={(e) => setLoginData({ ...loginData, email: e.target.value })}
                        className="h-12"
                        data-testid="login-email"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="login-password">Password</Label>
                      <Input
                        id="login-password"
                        type="password"
                        placeholder="Enter your password"
                        value={loginData.password}
                        onChange={(e) => setLoginData({ ...loginData, password: e.target.value })}
                        className="h-12"
                        data-testid="login-password"
                      />
                    </div>
                    <Button 
                      type="submit" 
                      className="w-full h-12 rounded-full bg-primary hover:bg-primary/90 font-semibold"
                      disabled={loading}
                      data-testid="login-submit"
                    >
                      {loading ? <Loader2 className="w-5 h-5 animate-spin" /> : 'Sign In'}
                    </Button>
                  </form>
                </TabsContent>

                <TabsContent value="register">
                  <form onSubmit={handleRegister} className="space-y-4">
                    <div className="space-y-2">
                      <Label htmlFor="register-name">Full Name</Label>
                      <Input
                        id="register-name"
                        type="text"
                        placeholder="John Smith"
                        value={registerData.name}
                        onChange={(e) => setRegisterData({ ...registerData, name: e.target.value })}
                        className="h-12"
                        data-testid="register-name"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="register-email">Email</Label>
                      <Input
                        id="register-email"
                        type="email"
                        placeholder="you@company.com"
                        value={registerData.email}
                        onChange={(e) => setRegisterData({ ...registerData, email: e.target.value })}
                        className="h-12"
                        data-testid="register-email"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="register-password">Password</Label>
                      <Input
                        id="register-password"
                        type="password"
                        placeholder="Create a password"
                        value={registerData.password}
                        onChange={(e) => setRegisterData({ ...registerData, password: e.target.value })}
                        className="h-12"
                        data-testid="register-password"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="register-confirm">Confirm Password</Label>
                      <Input
                        id="register-confirm"
                        type="password"
                        placeholder="Confirm your password"
                        value={registerData.confirmPassword}
                        onChange={(e) => setRegisterData({ ...registerData, confirmPassword: e.target.value })}
                        className="h-12"
                        data-testid="register-confirm"
                      />
                    </div>
                    <Button 
                      type="submit" 
                      className="w-full h-12 rounded-full bg-primary hover:bg-primary/90 font-semibold"
                      disabled={loading}
                      data-testid="register-submit"
                    >
                      {loading ? <Loader2 className="w-5 h-5 animate-spin" /> : 'Create Account'}
                    </Button>
                  </form>
                </TabsContent>
              </Tabs>
            </CardContent>
          </Card>

          <p className="text-center text-sm text-slate-500 mt-6">
            New staff? Register and wait for admin approval to get started.
          </p>
          <div className="text-center mt-3">
            <a href="/" className="inline-flex items-center gap-1.5 text-sm font-medium text-primary hover:text-primary/80 transition-colors" data-testid="back-to-portal-btn">
              <ArrowRight className="w-4 h-4 rotate-180" />
              Back to Customer Comparison Tool
            </a>
          </div>
            </>
          )}
      </div>
    </div>
  );
}
